import React from 'react';

import { IdToken, LocalStorageCache, useAuth0, User } from '@auth0/auth0-react';
import { useLocation } from '@reach/router';

import Alerts from '@components/Alerts';
import Footer from '@components/Footer';
import Header from '@components/Header';
import Loading from '@components/Loading';
import { MaintenanceMode } from '@presenters/web/pages/MaintenanceMode';

import useAuth0Hook from '../hooks/useAuth0Hook';

import { NOTIFICATIONS_WRAPPER_ID } from '@domain/notifications';

import {
  FEATURE_AUTH0_LOGIN,
  FEATURE_SITECORE_FOOTER,
  FEATURE_SITECORE_HEADER,
  isEnabled,
} from '@utils/features';
import { localizedNavigate } from '@utils/localized-navigate';

import { useAppConfig } from '@domui-hooks/appConfig';
import SitecoreFooter from '@domui-sitecore/wrappers/SitecoreFooter';
import SitecoreHeader from '@domui-sitecore/wrappers/SitecoreHeader';
import { isProtectedPath } from '@domui-utils/secureRoute';

import ErrorBoundary from '@hooks/error-boundary';
import { log } from '@hooks/logger';

import { LogLevel } from '@typings/graphql';

const Auth0Layout: React.FC = ({ children }) => {
  const {
    isAuthenticated,
    getAccessTokenSilently,
    getIdTokenClaims,
    user: auth0UserInfo,
    isLoading,
    loginWithRedirect = () => {},
    logout = () => {},
  } = useAuth0();
  const [accessToken, setAccessToken] = React.useState<string>();
  const [refreshToken, setRefreshToken] = React.useState<string | undefined>();
  const [idTokenClaims, setIdTokenClaims] = React.useState<
    IdToken | undefined
  >();
  const [auth0User, setAuth0User] = React.useState<User | undefined>();
  const isAuth0LoginEnabled = isEnabled(FEATURE_AUTH0_LOGIN);
  const { language = 'en', isAuth0Initializing } = useAppConfig();
  const { isMaintenanceMode } = useAuth0Hook(
    accessToken || '',
    refreshToken || '',
    idTokenClaims,
    auth0User
  );

  const location = useLocation();

  React.useEffect(() => {
    if (isAuth0LoginEnabled) {
      log({
        level: LogLevel.Info,
        message: 'QQ Auth0:: accessToken Fetch Started!',
      });
      if (!isAuthenticated) {
        log({
          level: LogLevel.Info,
          message: `QQ Auth0:: User Not Authenticated Yet!`,
        });
        return; // Wait until user is authenticated
      }

      const fetchData = async () => {
        try {
          const accessToken = await getAccessTokenSilently();
          const idTokenClaims = await getIdTokenClaims();

          // get refresh token from auth0-spa-js local storage cache
          const auth0Cache = new LocalStorageCache();
          const key = auth0Cache
            .allKeys()
            .find(key => key.includes('auth0spa'));

          type Auth0RefreshTokenResponse = {
            body?: {
              // eslint-disable-next-line camelcase
              refresh_token?: string;
            };
          };

          const refreshTokenValue = auth0Cache.get(key as string);
          const { refresh_token: refreshTokenFromStorage } =
            (refreshTokenValue as Auth0RefreshTokenResponse)?.body ?? {};
          const finalRefreshToken = refreshTokenFromStorage;

          if (!accessToken) {
            log({
              level: LogLevel.Info,
              message: 'QQ Auth0:: accessToken not available!',
            });
            return;
          }

          // eslint-disable-next-line no-underscore-dangle
          if (!idTokenClaims?.__raw) {
            log({
              level: LogLevel.Info,
              message: 'idTokenClaims.__raw is not available!',
            });
          }
          setAccessToken(accessToken);
          setRefreshToken(finalRefreshToken);
          setIdTokenClaims(idTokenClaims);
          setAuth0User(auth0UserInfo);
        } catch (err) {
          log({
            level: LogLevel.Error,
            message: `QQ Failed to fetch user info: ${JSON.stringify(err)}`,
          });
          localStorage.clear();
          sessionStorage.clear();
          await logout({ logoutParams: { returnTo: window.location.origin } });
        }
      };

      fetchData();
    }
  }, [isAuth0LoginEnabled, isAuthenticated]);

  React.useEffect(() => {
    const pathName =
      typeof window !== 'undefined' ? window.location.pathname : '/';
    const loginRequired =
      isAuth0LoginEnabled && !auth0UserInfo?.email && !isLoading;
    const isImpersonationFlow =
      typeof window !== 'undefined' &&
      window.localStorage.getItem('isImpersonationFlow') === 'true';
    log({
      level: LogLevel.Info,
      message: `Auth0Layout:: isImpersonationFlow ${isImpersonationFlow}`,
    });
    // While maintenance mode is on, only /maintenance-login should ever
    // initiate an Auth0 redirect (it does so explicitly, via its own
    // LoginPage instance). Every other protected path must fall through to
    // the maintenance banner instead - otherwise a logged-out Okta user
    // (whose session was just destroyed by maintenance-mode enforcement)
    // gets swept into the Auth0 hosted login here, before ever seeing the
    // banner.
    if (
      isProtectedPath(pathName, language) &&
      loginRequired &&
      !isImpersonationFlow &&
      !isMaintenanceMode
    ) {
      loginWithRedirect({
        authorizationParams: {
          ui_locales: language,
        },
        appState: { returnTo: location.pathname + location.search },
      });
    }
  }, [isLoading, auth0UserInfo?.email, language, isMaintenanceMode]);

  React.useEffect(() => {
    if (isAuth0LoginEnabled && isAuthenticated && !isLoading) {
      const segments = location.pathname.split('/').filter(Boolean);
      const lastSegment = segments[segments.length - 1];

      // /maintenance-login renders the same LoginPage component as /login
      // (see maintenance-login.tsx) - an already-authenticated user landing
      // on it directly needs the same "navigate away" treatment, or they're
      // stuck on a page that never redirects them anywhere.
      const isLoginPage =
        lastSegment === 'login' || lastSegment === 'maintenance-login';

      const destination = window.localStorage.getItem('destination');

      if (destination) {
        window.location.href = destination;
      } else {
        isLoginPage && localizedNavigate('/');
      }
    }
  }, [isAuth0LoginEnabled, isAuthenticated, isLoading]);

  const isOnAuth0LogoutPage = location.pathname.includes('/domui/auth0logout');
  const isOnMaintenanceLoginPage = location.pathname.includes(
    '/maintenance-login'
  );

  // A genuinely Auth0-authenticated user should never see the maintenance
  // banner, on any page - only Okta sessions (and anyone not yet
  // authenticated via Auth0) get blocked. Backend enforcement already only
  // force-logs-out Okta sessions; this is the matching frontend behavior.
  const isAuth0Authenticated = isAuth0LoginEnabled && isAuthenticated;

  // This must run BEFORE the maintenance-mode check below: on initial
  // mount the Auth0 SDK starts with isLoading=true/isAuthenticated=false
  // (it hasn't yet checked for a valid existing session), so checking the
  // banner condition first would show it briefly for every returning
  // Auth0 user, until the SDK resolves a moment later. Waiting out the
  // loading state here avoids that flash.
  if (
    isAuth0LoginEnabled &&
    (isLoading ||
      (isAuthenticated && (!accessToken || !idTokenClaims)) ||
      (isAuthenticated && isAuth0Initializing))
  ) {
    return (
      <div className="fixed inset-0 flex justify-center bg-white z-50">
        <Loading />
      </div>
    );
  }

  if (
    isMaintenanceMode &&
    !isOnAuth0LogoutPage &&
    !isOnMaintenanceLoginPage &&
    !isAuth0Authenticated
  ) {
    return <MaintenanceMode />;
  }

  return isAuth0LoginEnabled ? (
    <div>
      <ErrorBoundary>{children}</ErrorBoundary>
    </div>
  ) : (
    <div className="mainContainer min-h-full">
      {isEnabled(FEATURE_SITECORE_HEADER) ? <SitecoreHeader /> : <Header />}
      <Alerts />
      <div id={NOTIFICATIONS_WRAPPER_ID} />
      <main className="flex-1 tablet:pt-1">
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>
      {isEnabled(FEATURE_SITECORE_FOOTER) ? <SitecoreFooter /> : <Footer />}
    </div>
  );
};

export default Auth0Layout;
